// Display-time RAG resolver for a single deliverable. Two soft defaults
// layer over the stored value so the table column never tells a different
// story to the inline-edit form's `applyRagDefaults`:
//
//   - Overdue (non-terminal + past due) → 'red'. A demand-action row,
//     regardless of what the user graded.
//   - Accepted with no stored RAG       → 'green'. Mirrors the form's
//     Accepted-Green soft default (Phase 3 F). Legacy / un-stamped accepted
//     rows previously displayed "no RAG" while the form pre-ticked Green,
//     which read to users as a divergence bug.
//
// Late submissions (`derived === 'late'`) are stamped with
// ragRating='amber' + ragSource='derived' by the backend on the transition
// to submitted (handlers/tracking.ts). The amber sits as a real stored
// value the user can override; this resolver doesn't need to layer a
// separate late→amber collapse because the stored value is already
// correct for new transitions.
//
// Terminal statuses (accepted / rejected / closed) short-circuit in
// deriveDeliverableStatus so a past-due accepted item keeps its stored
// (or defaulted) RAG instead of flipping to red. Archived rows
// short-circuit before any branch because they're historical and not
// awaiting action.

import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import type { Deliverable, RagRating } from "../../../../types";

export function deriveEffectiveRag(
  deliverable: Deliverable,
  today?: Date,
): RagRating | null {
  if (deliverable.archivedAt) return deliverable.ragRating ?? null;
  const derived = deriveDeliverableStatus(
    deliverable.status,
    deliverable.dueDate,
    today,
    deliverable.submittedAt,
  );
  if (derived === "overdue") return "red";
  if (deliverable.ragRating) return deliverable.ragRating;
  // Stored RAG is null/undefined — apply the Accepted-Green soft default
  // so the table mirrors the inline-edit form's pre-ticked Green radio.
  if (deliverable.status === "accepted") return "green";
  return null;
}
